import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  Typography,
  Card,
  Grid,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatRelative } from 'date-fns';
import ShareIcon from '@mui/icons-material/Share';
import InsightsIcon from '@mui/icons-material/Insights';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BuildIcon from '@mui/icons-material/Build';
import TokenIcon from '@mui/icons-material/Token';
import StorageIcon from '@mui/icons-material/Storage';
import AppsIcon from '@mui/icons-material/Apps';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

import { useUserContext } from '@/contexts/userContext';
import TabPanel from '@/components/tabPanel';
import EntityAccessControl from '@/components/entities/administration/accessControl/accessControl';
import AuditRecords from '@/components/entities/administration/auditRecords/auditRecords';
import EntityLlmUsages from '@/components/entities/administration/usageStats/llmUsages';
import { EntityType } from '@/components/entities/entityType';

type Props = {
  pipeline: any;
  isOpen: boolean;
  maxHeight?: number;
  onDelete: () => void;
};

const StatCard = ({ title, value, icon, subtitle }: any) => (
  <Card
    variant="outlined"
    sx={{
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'flex-start',
      borderRadius: '12px',
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
      boxShadow: 'none',
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box
        sx={{
          bgcolor: 'rgba(0,0,0,0.03)',
          p: 0.75,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiSvgIcon-root': { fontSize: 18, color: 'secondary.main' },
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          fontSize: '0.65rem',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {title}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: subtitle ? 0.25 : 0, lineHeight: 1 }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.625rem', mt: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </Card>
);

const PipelineAdministration = ({ pipeline, isOpen, maxHeight = 500, onDelete }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasPermission } = useUserContext();

  const canDelete =
    pipeline?.userAccess?.role === 'owner' ||
    hasPermission('pipeline:delete', pipeline?.userAccess) ||
    hasPermission('pipeline:accessControl:manage', pipeline?.userAccess);

  const canViewAccessControl =
    pipeline?.userAccess?.role === 'owner' ||
    hasPermission('pipeline:accessControl:view', pipeline?.userAccess);

  const canViewUsageStats =
    pipeline?.userAccess?.role === 'owner' ||
    hasPermission('pipeline:usageStats:view', pipeline?.userAccess);

  const canViewAuditTrail =
    pipeline?.userAccess?.role === 'owner' ||
    hasPermission('pipeline:auditTrail:view', pipeline?.userAccess);

  const [selectedTab, setSelectedTab] = useState<string>(
    canViewAccessControl ? 'accessControl' : canViewUsageStats ? 'usageStats' : 'auditTrail',
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const formatSafeDate = (date: any) => {
    if (!date) return '';
    try {
      return formatRelative(new Date(date), new Date());
    } catch {
      return '';
    }
  };

  const items = pipeline?.items || [];
  const dataSources = useMemo(
    () => items.filter((item: any) => item.entityType === EntityType.dataSourceInstance),
    [items],
  );
  const agents = useMemo(
    () => items.filter((item: any) => item.entityType === EntityType.agentInstance),
    [items],
  );
  const userApps = useMemo(
    () => items.filter((item: any) => item.entityType === EntityType.userAppInstance),
    [items],
  );

  const subPanelHeight = Math.max(250, maxHeight - 140);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 2 }}>
      {/* Top Metadata & Administration Actions Banner */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: '12px',
          bgcolor: 'background.paper',
          mb: 1.5,
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountTreeIcon color="secondary" sx={{ fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {pipeline?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({pipeline?.blueprintId || 'Pipeline Instance'})
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
              {pipeline?.createdDate && (
                <Typography variant="caption" color="text.secondary">
                  {t('common.created')}: {formatSafeDate(pipeline.createdDate)} by {pipeline.createdBy}
                </Typography>
              )}
              {pipeline?.lastUpdatedDate && (
                <Typography variant="caption" color="text.secondary">
                  {t('common.updated')}: {formatSafeDate(pipeline.lastUpdatedDate)} by{' '}
                  {pipeline.lastUpdatedBy || pipeline.createdBy}
                </Typography>
              )}
            </Stack>
          </Stack>

          {canDelete && (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={onDelete}
              startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '8px',
                px: 2,
                boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                },
              }}
            >
              {t('common.buttons.deletePipeline', 'Delete Pipeline')}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Administration Sub-tabs Bar */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          borderRadius: '12px 12px 0 0',
          px: 1,
          flexShrink: 0,
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="secondary"
          sx={{
            minHeight: 40,
            '& .MuiTabs-indicator': {
              height: '3px',
              borderRadius: '3px 3px 0 0',
              bgcolor: '#8BD8BC',
            },
          }}
        >
          {canViewAccessControl && (
            <Tab
              value="accessControl"
              icon={<ShareIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={
                <Typography variant="caption" sx={{ fontWeight: selectedTab === 'accessControl' ? 700 : 500 }}>
                  {t('entity.administration.tabs.accessControl', 'Access Control')}
                </Typography>
              }
              sx={{ minHeight: 40, textTransform: 'none', px: 2 }}
            />
          )}

          {canViewUsageStats && (
            <Tab
              value="usageStats"
              icon={<InsightsIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={
                <Typography variant="caption" sx={{ fontWeight: selectedTab === 'usageStats' ? 700 : 500 }}>
                  {t('entity.administration.tabs.usageStats', 'Usage Stats')}
                </Typography>
              }
              sx={{ minHeight: 40, textTransform: 'none', px: 2 }}
            />
          )}

          {canViewAuditTrail && (
            <Tab
              value="auditTrail"
              icon={<HistoryIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={
                <Typography variant="caption" sx={{ fontWeight: selectedTab === 'auditTrail' ? 700 : 500 }}>
                  {t('entity.administration.tabs.auditTrail', 'Audit Trail')}
                </Typography>
              }
              sx={{ minHeight: 40, textTransform: 'none', px: 2 }}
            />
          )}
        </Tabs>
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden', mt: 1 }}>
        {isOpen && (
          <>
            {/* Access Control Panel */}
            {canViewAccessControl && (
              <TabPanel value="accessControl" currentTab={selectedTab} maxHeight={subPanelHeight}>
                <EntityAccessControl
                  entity={{
                    ...pipeline,
                    entityType: EntityType.pipelineInstance,
                    entityDefId: pipeline?.blueprintId || '',
                  }}
                  maxHeight={subPanelHeight}
                />
              </TabPanel>
            )}

            {/* Usage Stats Panel */}
            {canViewUsageStats && (
              <TabPanel value="usageStats" currentTab={selectedTab} maxHeight={subPanelHeight}>
                <Stack spacing={2} sx={{ height: '100%', overflowY: 'auto', pr: 0.5 }}>
                  {/* Pipeline Overview Stat Cards */}
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                      <StatCard
                        title="Data Sources"
                        value={dataSources.length}
                        icon={<StorageIcon />}
                        subtitle="Ingestion connections"
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                      <StatCard
                        title="Autonomous Agents"
                        value={agents.length}
                        icon={<PsychologyIcon />}
                        subtitle="ReAct reasoning agents"
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                      <StatCard
                        title="User Apps"
                        value={userApps.length}
                        icon={<AppsIcon />}
                        subtitle="Interactive canvases"
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <StatCard
                        title="Connected Items"
                        value={items.length}
                        icon={<AccountTreeIcon />}
                        subtitle="Total pipeline nodes"
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <StatCard
                        title="Pipeline Status"
                        value={pipeline?.active !== false ? 'Active' : 'Paused'}
                        icon={<InsightsIcon />}
                        subtitle="Execution readiness"
                      />
                    </Grid>
                  </Grid>

                  {/* LLM Usage Records Table */}
                  <Box sx={{ flexGrow: 1, minHeight: 350 }}>
                    <EntityLlmUsages
                      entity={{
                        ...pipeline,
                        entityType: EntityType.pipelineInstance,
                        entityDefId: pipeline?.blueprintId || '',
                      }}
                    />
                  </Box>
                </Stack>
              </TabPanel>
            )}

            {/* Audit Trail Panel */}
            {canViewAuditTrail && (
              <TabPanel value="auditTrail" currentTab={selectedTab} maxHeight={subPanelHeight}>
                <AuditRecords
                  entity={{
                    ...pipeline,
                    entityType: EntityType.pipelineInstance,
                    entityDefId: pipeline?.blueprintId || '',
                  }}
                  maxHeight={subPanelHeight}
                />
              </TabPanel>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default PipelineAdministration;
